import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button, Col, FloatingLabel, Row, Form, Table } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import { Link, useNavigate } from "react-router-dom";
import Swal from 'sweetalert2';
import moment from 'moment';
import "../../../assets/sass/MycustomCSS.css"

const DraftDistribution = () => {
  const navigate = useNavigate();

  /* ───────────────────────── STATE ───────────────────────── */
  const [identifiers,       setIdentifiers]       = useState([]);
  const [families,          setFamilies]          = useState([]);
  const [verifiers,         setVerifiers]         = useState([]);
  const [selectedVerifier,  setSelectedVerifier]  = useState("");
  const [distributionDate, setDistributionDate] = useState(moment().format("YYYY-MM-DD"));
  const [selectedFamilyIds, setSelectedFamilyIds] = useState([]);     // NEW
  const [search, setSearch] = useState("");

  /* ──────────────────────── AUTH HEADER ──────────────────────── */
  const AuthToken = localStorage.getItem("AuthUser");
  axios.defaults.headers.common["Authorization"] = `Bearer ${AuthToken}`;

  /* ─────────────────────── INITIAL DATA ─────────────────────── */
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/verification-initial-data`)
      .then(res => setIdentifiers(res.data.organizations))
      .catch(console.error);
  }, []);

  /* ─────────── when organization changes, pull families ─────────── */
  const handleChangeIdentifier = e => {
    const id = e.target.value;
    if (!id) return;

    axios.get(`${process.env.REACT_APP_API_URL}/selected-identifier-for-verification/${id}`)
      .then(res => {
        setFamilies(res.data.people);
        setVerifiers(res.data.verifiers);
        setSelectedVerifier("");
        setSelectedFamilyIds([]);                 // reset selections
        setSearch("");
      })
      .catch(console.error);
  };

  /* ─────────── INDIVIDUAL & MASTER CHECKBOX LOGIC ─────────── */
  const toggleFamily = id => {
    setSelectedFamilyIds(prev =>
      prev.includes(id) ? prev.filter(fid => fid !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    const filteredIds = filteredFamilies.map(f => f.id);
    if (filteredIds.every(id => selectedFamilyIds.includes(id))) {
      setSelectedFamilyIds(selectedFamilyIds.filter(id => !filteredIds.includes(id)));
    } else {
      setSelectedFamilyIds([...new Set([...selectedFamilyIds, ...filteredIds])]);
    }
  };

  const searchTerm = search.trim().toLowerCase();
  const filteredFamilies = families.filter(fam =>
    !searchTerm ||
    fam.f_number?.toString().toLowerCase().includes(searchTerm) ||
    fam.beneficiary_name?.toLowerCase().includes(searchTerm) ||
    fam.mob?.toString().toLowerCase().includes(searchTerm) ||
    fam.city?.name?.toLowerCase().includes(searchTerm)
  );

  /* ───────────────────────── SUBMIT ───────────────────────── */
  const handleSubmit = () => {
    if (!selectedVerifier) {
      Swal.fire("Warning", "Please select a verifier", "warning");
      return;
    }
    if (selectedFamilyIds.length === 0) {
      Swal.fire("Warning", "Please select at least one family", "warning");
      return;
    }
    if (!distributionDate) {
      Swal.fire("Warning", "Please select distribution date", "warning");
      return;
    }

    axios.post(`${process.env.REACT_APP_API_URL}/distribute-verification`, {
      verifier_id: selectedVerifier,
      family_ids : selectedFamilyIds,

      distribution_date: distributionDate,
    })
    .then(() => {
      Swal.fire("Success", "Selected families distributed", "success");
      navigate('/distribution-dashboard');
    })
    .catch(err => {
      Swal.fire("Error", "Failed to distribute families", "error");
      console.error(err);
    });
  };

  /* ───────────────────────── JSX ───────────────────────── */
  return (
    <PageLayout>
      {/* breadcrumb, organization select, counters */}
      <Row>
        <Col md={6}>
          <FloatingLabel label="Select Organization" className="mb-3">
            <Form.Select onChange={handleChangeIdentifier}>
              <option value="">Select...</option>
              {identifiers.map(i => (
                <option key={i.id} value={i.id}>{i.name}</option>
              ))}
            </Form.Select>
          </FloatingLabel>
        </Col>

        <Col md={3}>
          <FloatingLabel label="Total families" className="mb-3">
            <Form.Control value={families.length} disabled />
          </FloatingLabel>
        </Col>

        <Col md={3}>
          <FloatingLabel label="Selected families" className="mb-3">
            <Form.Control value={selectedFamilyIds.length} disabled />
          </FloatingLabel>
        </Col>
      </Row>

      {/* families table with checkboxes */}
      {families.length > 0 && (
        <>
          <Row className="mb-3">
            <Col md={4}>
              <FloatingLabel label="Search FSF-ID / Name / Contact / City">
                <Form.Control
                  type="text"
                  placeholder="Search FSF-ID / Name / Contact / City"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </FloatingLabel>
            </Col>
          </Row>
          <Row>
            <Col>
              <h5>Families List</h5>
              <Table striped bordered hover size="sm" responsive>
                <thead>
                  <tr>
                    <th style={{width:"40px"}}>
                      <Form.Check
                        type="checkbox"
                        checked={filteredFamilies.length > 0 && filteredFamilies.every(f => selectedFamilyIds.includes(f.id))}
                        onChange={toggleSelectAll}
                      />
                    </th>
                    <th>#</th>
                    <th>Family Name</th>
                    <th>Contact</th>
                    <th>Debt Amount</th>
                    <th>City</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFamilies.map(fam => (
                    <tr key={fam.id}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={selectedFamilyIds.includes(fam.id)}
                          onChange={() => toggleFamily(fam.id)}
                        />
                      </td>
                      <td>{fam.f_number}</td>
                      <td>{fam.beneficiary_name || 'N/A'}</td>
                      <td>{fam.mob || '-'}</td>
                      <td>{fam.loan.debt_ledger || '-'}</td>
                      <td>{fam.city?.name || '-'}</td>
                    </tr>
                  ))}
                  {filteredFamilies.length === 0 && (
                    <tr><td colSpan={6} className="text-center">No families match your search.</td></tr>
                  )}
                </tbody>
              </Table>
            </Col>
          </Row>

          {/* verifier selector */}
          <Row>
            <Col md={8}>
              <FloatingLabel label="Select Verifier" className="mb-3">
                <Form.Select value={selectedVerifier} onChange={e => setSelectedVerifier(e.target.value)}>
                  <option value="">Select verifier</option>
                  {verifiers.map(v => (
                    <option key={v.id} value={v.id}>{v.name}</option>
                  ))}
                </Form.Select>
              </FloatingLabel>
            </Col>
            <Col md={4}>
              <FloatingLabel label="Distribution Date" className="mb-3">
                <Form.Control type="date" value={distributionDate} onChange={e => setDistributionDate(e.target.value)} />
              </FloatingLabel>
            </Col>
          </Row>
        </>
      )}

      <Button className="mt-3"  onClick={handleSubmit}
      style={{backgroundColor:"#077834"}}>
        Submit
      </Button>
    </PageLayout>
  );
};

export default DraftDistribution;
